import React__default, { useCallback, Children, useMemo } from 'react';

class HappyWebSystemError extends Error {
    constructor(context, message){
        super(`${typeof context === "string" ? context : context.displayName}: ${message}`);
    }
}
const isMatchedChild = (child)=>{
    return child?.type?.dataId !== undefined;
};
const isFragment = (child)=>{
    return child?.type === React__default.Fragment;
};
const isElement = (child)=>{
    return child?.props !== undefined && child?.props?.children !== undefined;
};
/**
 * Parses provided children for specified system components, with matches and other components returned 'flattened'.
 * Allows parsing a set depth of children within the provided child tree.
 */ const useQueryChildComponents = (children, components, depth = 0)=>{
    const queryChildren = useCallback((queryableChildren, components)=>{
        const matchedChildren = [];
        const otherChildren = [];
        const allChildren = Children.toArray(queryableChildren);
        const dataIds = components.map((comp)=>comp?.dataId);
        for (const child of allChildren){
            if (!!child && isMatchedChild(child) && dataIds.includes(child?.type?.dataId)) {
                // `Any` usage is due to the element typing of components getting too convoluted with potential valid React Child
                // types. Due to the data ID property check, the type _must_ be one of our self created System Components.
                matchedChildren.push(child);
            } else if (!!child && isFragment(child)) {
                // Ignore a top level fragment when trying to parse child components. Fragments can be used to group components
                // when conditionally displaying UI, so may not necessarily affect the expected DOM tree.
                const fragmentChildren = Children.toArray(child.props.children);
                for (const fragmentChild of fragmentChildren){
                    if (!!fragmentChild && isMatchedChild(fragmentChild) && dataIds.includes(fragmentChild?.type?.dataId)) {
                        matchedChildren.push(fragmentChild);
                    } else {
                        otherChildren.push(fragmentChild);
                    }
                }
            } else {
                otherChildren.push(child);
            }
        }
        return [
            matchedChildren,
            otherChildren
        ];
    }, []);
    const matched = useMemo(()=>{
        let matchedChildren = [];
        let otherChildren = [];
        const [match, others] = queryChildren(children, components);
        matchedChildren = matchedChildren.concat(match);
        otherChildren = otherChildren.concat(others);
        // Allow another iteration through the child components. Currently this is limited to a depth of 1 to ensure the
        // query is performant.
        if (depth === 1) {
            // Clear the other children already found as they may include possible matches, which would result in the same
            // component being returned 'twice' in the matches and the 'others' component tree.
            const depthParseChildren = [
                ...otherChildren
            ];
            otherChildren = [];
            for (const child of depthParseChildren){
                if (isElement(child)) {
                    const [depthMatch, depthOthers] = queryChildren(child?.props?.children, components);
                    // Flatten the matched children
                    matchedChildren = matchedChildren.concat(depthMatch);
                    // As the child's children have already been parsed, do not include them directly
                    const childWithRemovedChildren = React__default.cloneElement(child, {
                        ...child.props,
                        children: null
                    });
                    otherChildren.push(childWithRemovedChildren);
                    // Flatten the 'other' returned components
                    otherChildren = otherChildren.concat(depthOthers);
                } else {
                    // This element cannot be parsed by our query anyway, let it passthrough.
                    otherChildren.push(child);
                }
            }
        }
        return [
            matchedChildren,
            otherChildren
        ];
    }, [
        children,
        components,
        depth,
        queryChildren
    ]);
    return matched;
};

export { HappyWebSystemError, useQueryChildComponents };
